import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { EngagementTable } from '../components/engagement-table';
import { RangePicker } from '../components/range-picker';
import { ReportExportButton } from '../components/report-export-button';
import {
  DEFAULT_ENGAGEMENT_SORT,
  useProductEngagementReport,
} from '../hooks/use-product-engagement-report';
import { useProductsReport } from '../hooks/use-products-report';
import { useReportRange } from '../hooks/use-report-range';

const ROUTE_ID = '/_authed/_active/s/$storeId/reports/products' as const;

function ProductsReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const format = useFormat();
  const { range, setRange } = useReportRange();
  const { engagementSort = DEFAULT_ENGAGEMENT_SORT } = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/s/$storeId/reports/products' });
  const query = useProductsReport(store.id, range);
  const engagement = useProductEngagementReport(store.id, range, engagementSort);
  const items = query.data?.items;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 lg:flex-row lg:items-start lg:justify-between">
        <RangePicker range={range} onChange={setRange} />
        <ReportExportButton storeId={store.id} report="products" range={range} />
        {query.data?.truncated ? (
          <p className="text-muted-foreground text-xs">{t('topN', { count: query.data.limit })}</p>
        ) : null}
      </div>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Card className="gap-0 overflow-hidden py-0" data-tour="reports.products.table">
          <Table aria-label={t('products.table')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">{t('columns.product')}</TableHead>
                <TableHead className="hidden md:table-cell">{t('columns.sku')}</TableHead>
                <TableHead className="text-right">{t('columns.orders')}</TableHead>
                <TableHead className="text-right">{t('columns.units')}</TableHead>
                <TableHead className="hidden text-right md:table-cell">
                  {t('columns.gross')}
                </TableHead>
                <TableHead className="hidden text-right lg:table-cell">
                  {t('columns.discounts')}
                </TableHead>
                <TableHead className="pr-5 text-right">{t('columns.net')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7} className="px-5">
                      <Skeleton className="h-5" />
                    </TableCell>
                  </TableRow>
                ))
              ) : items && items.length > 0 ? (
                items.map((item, i) => (
                  <TableRow key={item.variantId ?? `${item.title}-${i}`}>
                    <TableCell className="pl-5">
                      {item.productId ? (
                        <Link
                          to="/org/products/$productId"
                          params={{ productId: item.productId }}
                          className="font-medium hover:underline"
                        >
                          {item.title}
                        </Link>
                      ) : (
                        <span className="font-medium">{item.title}</span>
                      )}
                      {item.variantTitle ? (
                        <span className="text-muted-foreground block text-xs">
                          {item.variantTitle}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden font-mono text-xs md:table-cell">
                      {item.sku ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.number(item.ordersCount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.number(item.units)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums md:table-cell">
                      {format.money(item.gross)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums lg:table-cell">
                      {format.money(item.discounts)}
                    </TableCell>
                    <TableCell className="pr-5 text-right tabular-nums">
                      {format.money(item.net)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                    {t('empty.noSales')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
      <EngagementTable
        storeId={store.id}
        sort={engagementSort}
        onSortChange={(sort) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              engagementSort: sort === DEFAULT_ENGAGEMENT_SORT ? undefined : sort,
            }),
            replace: true,
          })
        }
        data={engagement.data}
        pending={engagement.isPending}
        error={engagement.error}
        onRetry={() => void engagement.refetch()}
      />
    </div>
  );
}

export function ProductsReportPage() {
  return (
    <FeatureErrorBoundary name="reports.products">
      <ProductsReport />
    </FeatureErrorBoundary>
  );
}
